import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { StatusSelect } from "@/components/StatusSelect";
import { SendSmsDialog } from "@/components/SendSmsDialog";
import { SiteAuditDialog } from "@/components/SiteAuditDialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 25;

const STATUS_TABS = [
  { value: "", label: "Tous" },
  { value: "nouveau", label: "Nouveau" },
  { value: "a_contacter", label: "A contacter" },
  { value: "contacte", label: "Contacte" },
  { value: "ignore", label: "Ignore" },
];

export default function ProspectsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(1);

  const fetchProspects = useCallback(async () => {
    try {
      const res = await api.get("/prospects", { params: { statut: statut || undefined, page, page_size: PAGE_SIZE } });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Erreur lors du chargement des prospects");
    }
  }, [statut, page]);

  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  useEffect(() => {
    setPage(1);
  }, [statut]);

  const updateStatus = async (placeId, newStatut) => {
    try {
      await api.patch(`/prospects/${placeId}/status`, { statut: newStatut });
      setItems((prev) => prev.map((it) => (it.place_id === placeId ? { ...it, statut: newStatut } : it)));
      toast.success("Statut mis a jour");
    } catch (e) {
      toast.error("Erreur mise a jour statut");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 space-y-4" data-testid="prospects-page">
      <div className="flex gap-1 border-b border-border">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`prospects-tab-${t.value || "tous"}`}
            onClick={() => setStatut(t.value)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-wider border-b-2 transition-colors duration-75 ${
              statut === t.value ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Nom</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Telephone</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Adresse</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Site Google</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Maps</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Statut</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">SMS</TableHead>
              <TableHead className="font-mono text-xs uppercase text-muted-foreground tracking-wider">Audit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.place_id} data-testid={`prospect-row-${p.place_id}`} className="border-border">
                <TableCell className="font-medium">{p.nom}</TableCell>
                <TableCell className="font-mono text-xs">{p.telephone || "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.adresse || "-"}</TableCell>
                <TableCell className="font-mono text-xs">{p.site_google ? "Oui" : "Non"}</TableCell>
                <TableCell>
                  {p.lien_google_maps && (
                    <a
                      href={p.lien_google_maps}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`prospect-maps-link-${p.place_id}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ExternalLink size={12} /> Voir
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <StatusSelect value={p.statut} onChange={(v) => updateStatus(p.place_id, v)} testId={`prospect-status-select-${p.place_id}`} />
                </TableCell>
                <TableCell>
                  <SendSmsDialog placeId={p.place_id} disabled={!p.telephone} />
                </TableCell>
                <TableCell>
                  <SiteAuditDialog placeId={p.place_id} disabled={!p.site_url} />
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground font-mono text-xs uppercase" data-testid="prospects-empty-state">
                  Aucun prospect
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between font-mono text-xs uppercase tracking-wider">
        <span data-testid="prospects-total-count">{total} prospect(s)</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-none"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            data-testid="prospects-prev-page"
          >
            <ChevronLeft size={14} />
          </Button>
          <span data-testid="prospects-page-indicator">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="rounded-none"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="prospects-next-page"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
